import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { chunk } from 'lodash-es';
import {
  Paper,
  List,
  ListItem,
  TextField,
  Box,
  Typography,
} from '@mui/material';

AdjustmentGroups.propTypes = {
  sx: PropTypes.object,
  multipleOf: PropTypes.number,
  floor: PropTypes.number,
  ceiling: PropTypes.number,
  fieldAdjustment: PropTypes.number,
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      adjustment: PropTypes.number,
      acres: PropTypes.number,
    }),
  ),
  onGroupAdjustments: PropTypes.func.isRequired,
  onFieldAdjustment: PropTypes.func.isRequired,
  onFeatureSelected: PropTypes.func,
  revertRef: PropTypes.object,
};

export function AdjustmentGroups(props) {
  const {
    sx = {},
    floor = 0,
    ceiling = Number.MAX_SAFE_INTEGER,
    multipleOf = 1,
    groups,
    onGroupAdjustments,
    onFieldAdjustment,
    onFeatureSelected = () => {},
    revertRef = {},
  } = props;

  revertRef.current = () => {
    handleFieldAdjustment({ target: { value: '' } });
  };

  const [fieldAdjustment, setFieldAdjustment] = useState(
    props.fieldAdjustment || '',
  );

  const [groupAdjustments, setGroupAdjustments] = useState(
    Object.fromEntries(
      groups.map((g) => [
        g.value,
        parseAdjustment(g.adjustment) +
          parseAdjustment(props.fieldAdjustment) || '',
      ]),
    ),
  );

  const groupValues = useMemo(
    () => Object.keys(groupAdjustments).map(Number),
    [groupAdjustments],
  );

  const columns = useMemo(() => {
    const distinct = [...new Set(groupValues.toSorted((a, b) => a - b))];

    if (distinct.length === 4) {
      return [distinct.slice(0, 2), distinct.slice(2, 3), distinct.slice(3)];
    }

    const numberOfColumns = 3;
    const itemsPerColumn = Math.ceil(distinct.length / numberOfColumns);
    return chunk(distinct, itemsPerColumn);
  }, [groupValues]);

  const getFieldAdjustment = useCallback(
    () => (isValid(fieldAdjustment) ? parseAdjustment(fieldAdjustment) : 0),
    [fieldAdjustment],
  );

  const getCappedGroupAdjustment = useCallback(
    (value, adjustment) => {
      const base = value + adjustment;
      const rounded = roundTo(base, multipleOf);
      const roundedAdjustment = adjustment + (rounded - base);

      if (rounded < floor) return roundedAdjustment + (floor - rounded);
      if (rounded > ceiling) return roundedAdjustment - (rounded - ceiling);
      return rounded - value;
    },
    [ceiling, floor, multipleOf],
  );

  const getGroupAdjustment = useCallback(
    (value) => {
      if (!isValid(groupAdjustments[value])) return 0;
      const adjustment = parseAdjustment(groupAdjustments[value]);
      const group = getCappedGroupAdjustment(value, adjustment);
      const field = getFieldAdjustment();
      return group - field;
    },
    [getCappedGroupAdjustment, getFieldAdjustment, groupAdjustments],
  );

  const [shouldPublishField, setPublishField] = useState(false);
  const [shouldPublishGroups, setPublishGroups] = useState(false);

  useEffect(publishFieldAdjustment, [
    fieldAdjustment,
    onFieldAdjustment,
    shouldPublishField,
  ]);

  useEffect(publishGroupAdjustments, [
    getFieldAdjustment,
    getGroupAdjustment,
    groupValues,
    groupAdjustments,
    onGroupAdjustments,
    shouldPublishGroups,
    groups,
  ]);

  return (
    <Box sx={sx}>
      {buildFullFieldAdjustment()}
      {buildGroupAdjustments()}
    </Box>
  );

  function buildFullFieldAdjustment() {
    return (
      <TextField
        data-testid={`full-field-adjustment`}
        variant="standard"
        size="small"
        label="Full Field Adjustment"
        InputLabelProps={{ shrink: true }}
        inputProps={{ autoComplete: 'off' }}
        value={fieldAdjustment}
        onChange={handleFieldAdjustment}
        error={!isValid(fieldAdjustment)}
      />
    );
  }

  function buildGroupAdjustments() {
    return (
      <Box sx={{ display: 'flex', gap: 2, p: 0.25 }}>
        {columns.map(buildColumn)}
      </Box>
    );
  }

  function buildColumn(values, i) {
    return (
      <Box sx={{ mt: 2 }} key={i}>
        <Paper>
          <List>
            {buildColumnHeader()}
            {values.map(buildColumnValue)}
          </List>
        </Paper>
      </Box>
    );
  }

  function buildColumnHeader() {
    return (
      <ListItem display="flex" sx={{ mb: -2, gap: 2, width: '100%' }}>
        <Typography sx={{ flex: 1, textAlign: 'right' }} variant="body2">
          Original
        </Typography>
        <Box sx={{ flex: 1.5 }} />
        <Typography sx={{ flex: 1 }} variant="body2">
          Adjusted
        </Typography>
      </ListItem>
    );
  }

  function buildColumnValue(value, i) {
    return (
      <ListItem key={i} display="flex" sx={{ mt: 2, gap: 2, width: '100%' }}>
        <Typography
          data-testid={`${value}-original`}
          sx={{ flex: 1, textAlign: 'right' }}
          variant="body1"
        >
          {value}
        </Typography>
        <TextField
          data-testid={`${value}-adjustment`}
          sx={{ flex: 1.5 }}
          variant="standard"
          size="small"
          value={groupAdjustments[value]}
          onChange={handleGroupAdjustment.bind(null, value)}
          onFocus={() => onFeatureSelected(value)}
          onBlur={() => onFeatureSelected(-1)}
          error={!isValid(groupAdjustments[value])}
          inputProps={{ autoComplete: 'off' }}
        />
        <Typography
          data-testid={`${value}-adjusted`}
          sx={{ flex: 1 }}
          variant="body1"
        >
          {value + getGroupAdjustment(value) + getFieldAdjustment()}
        </Typography>
      </ListItem>
    );
  }

  function handleFieldAdjustment(e) {
    const adjustment = e.target.value;
    setFieldAdjustment(adjustment);
    setPublishField(true);

    if (isValid(adjustment)) {
      adjustGroups(
        setGroupAdjustments(
          Object.fromEntries(groupValues.map((v) => [v, adjustment])),
        ),
      );
    }
  }

  function handleGroupAdjustment(value, e) {
    adjustGroups({ [value]: e.target.value });
  }

  function adjustGroups(newAdjustments) {
    setGroupAdjustments((current) => ({ ...current, ...newAdjustments }));
    setPublishGroups(true);
  }

  function publishFieldAdjustment() {
    if (!shouldPublishField) return;
    setPublishField(false);

    if (isValid(fieldAdjustment)) {
      onFieldAdjustment(parseAdjustment(fieldAdjustment));
    }
  }

  function publishGroupAdjustments() {
    if (!shouldPublishGroups) return;
    setPublishGroups(false);

    if (Object.values(groupAdjustments).every(isValid)) {
      onGroupAdjustments(
        groups.map((g) => {
          const adjustment = getGroupAdjustment(g.value);
          const totalAdjustment = getFieldAdjustment() + adjustment;
          return { ...g, adjustment, adjustedValue: g.value + totalAdjustment };
        }),
      );
    }
  }
}

function parseAdjustment(adjustment) {
  if (isTryingToEnterNegative(adjustment)) return 0;
  return adjustment ? Number(adjustment) : 0;
}

function isTryingToEnterNegative(adjustment) {
  return (
    typeof adjustment === 'string' && ['-', ''].includes(adjustment.trim())
  );
}

function isValid(value) {
  if (isTryingToEnterNegative(value)) return true;
  return !Number.isNaN(Number(value));
}

function roundTo(value, multiple) {
  return Math.round(value / multiple) * multiple;
}
